'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AmbientWorldEventKind,
  ambientWorldCatalog,
  parseAmbientWorldEventKind,
} from '@/lib/fishing-world-catalog'
import {
  advanceFishingWorldScheduler,
  completeFishingWorldEvent,
  createFishingWorldSeededRandom,
  emptyFishingWorldSchedulerState,
  type FishingWorldActiveEvent,
  type FishingWorldSchedulerState,
  type FishingWorldSchedulerDecision,
} from '@/lib/fishing-world-scheduler'
import {
  fishingWorldAmbientAdmissionRejection,
  type FishingWorldAmbientAdmissionRejection,
} from '@/lib/fishing-world-render-contract'
import { createMicroLifePresentation, validateMicroLifePlacementForKind } from '@/lib/fishing-world-anchors'
import { isEnabledFishingWorldDebugSimulation, parseFishingWorldDebugSimulation } from '@/lib/fishing-world-debug'
import { advanceFishingWorldLegacyAmbient } from '@/lib/fishing-world-legacy-ambient'
import {
  createFishingWorldRolloutObserver,
  disabledFishingWorldRollout,
  runFishingWorldRollout,
  type FishingWorldRolloutControl,
  type FishingWorldSchedulerAdapter,
} from '@/lib/fishing-world-rollout'

const SCHEDULER_TICK_MS = 1_000

export type FishingWorldForceOptions = Readonly<{
  badgerSide?: 'left' | 'right'
}>
export type FishingWorldDebugRejection = Readonly<{
  kind: AmbientWorldEventKind
  reason: FishingWorldAmbientAdmissionRejection | 'invalid-placement' | 'lifecycle-suppressed'
}>
function isDocumentVisible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

function readsReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * The only runtime producer for cosmetic fishing-world events. Renderers are
 * deliberately consumers: they receive an event instance and only report that
 * same instance complete.
 */
export type FishingWorldRuntimeOptions = Readonly<{
  rollout?: FishingWorldRolloutControl
  legacyAmbient?: FishingWorldSchedulerAdapter
  sharedScheduler?: FishingWorldSchedulerAdapter
}>

export function useFishingWorldRuntime(options: FishingWorldRuntimeOptions = {}) {
  const rollout = options.rollout ?? disabledFishingWorldRollout
  const legacyAmbient = options.legacyAmbient ?? advanceFishingWorldLegacyAmbient
  const sharedScheduler = options.sharedScheduler ?? advanceFishingWorldScheduler
  const schedulerState = useRef<FishingWorldSchedulerState>(emptyFishingWorldSchedulerState)
  const seed = useRef(Date.now() >>> 0)
  const random = useRef(createFishingWorldSeededRandom(seed.current))
  const forcedSequence = useRef(0)
  const rolloutObserver = useRef(createFishingWorldRolloutObserver())
  const [activeEvents, setActiveEvents] = useState<readonly FishingWorldActiveEvent[]>([])
  const [visible, setVisible] = useState(isDocumentVisible)
  const [reducedMotion, setReducedMotion] = useState(readsReducedMotion)
  const [lastDecision, setLastDecision] = useState<FishingWorldSchedulerDecision | null>(null)
  const [debugRejection, setDebugRejection] = useState<FishingWorldDebugRejection | null>(null)

  const publish = useCallback((next: FishingWorldSchedulerState) => {
    schedulerState.current = next
    setActiveEvents(next.active)
  }, [])

  const advance = useCallback(() => {
    const schedulerInput = {
      clock: Date.now,
      random: random.current,
      catalog: ambientWorldCatalog,
      state: schedulerState.current,
      lifecycle: { visible, reducedMotion },
    }
    const { result } = runFishingWorldRollout(
      rollout,
      schedulerInput,
      legacyAmbient,
      sharedScheduler,
      rolloutObserver.current,
    )
    publish(result.state)
    setLastDecision(result.decision)
  }, [legacyAmbient, publish, reducedMotion, rollout, sharedScheduler, visible])

  useEffect(() => {
    const onVisibilityChange = () => setVisible(isDocumentVisible())
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(query.matches)
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])

  useEffect(() => {
    advance()
    if (!visible) return
    const timer = window.setInterval(advance, SCHEDULER_TICK_MS)
    return () => window.clearInterval(timer)
  }, [advance, visible])

  const completeAmbientEvent = useCallback((eventId: string) => {
    publish(completeFishingWorldEvent(schedulerState.current, eventId))
  }, [publish])

  const forceAmbientEvent = useCallback((value: unknown, options: FishingWorldForceOptions = {}): boolean => {
    const kind = parseAmbientWorldEventKind(value)
    if (!kind) return false
    const policy = ambientWorldCatalog[kind]
    if (
      !visible
      || !policy.enabled
      || (reducedMotion && policy.reducedMotion === 'suppress')
    ) {
      setDebugRejection({ kind, reason: 'lifecycle-suppressed' })
      return false
    }

    const nowMs = Date.now()
    const active = schedulerState.current.active.filter((event) => event.expiresAtMs > nowMs
      && !(kind === AmbientWorldEventKind.Badger && event.kind === AmbientWorldEventKind.Badger))
    const admissionRejection = fishingWorldAmbientAdmissionRejection(active, kind)
    if (admissionRejection) {
      setDebugRejection({ kind, reason: admissionRejection })
      return false
    }

    const eventId = `debug:${kind}:${nowMs}:${++forcedSequence.current}`
    const placement = createMicroLifePresentation(kind, `debug:${kind}`)
    if (!placement || placement.id !== policy.placement || validateMicroLifePlacementForKind(kind, placement) !== null) {
      setDebugRejection({ kind, reason: 'invalid-placement' })
      return false
    }
    const event: FishingWorldActiveEvent = {
      id: eventId,
      kind,
      startedAtMs: nowMs,
      expiresAtMs: nowMs + policy.durationMs,
      presentation: {
        placement,
        ...(kind === AmbientWorldEventKind.Badger && options.badgerSide ? { badgerSide: options.badgerSide } : {}),
      },
    }
    publish({ ...schedulerState.current, active: [...active, event] })
    setDebugRejection(null)
    return true
  }, [publish, reducedMotion, visible])

  const simulateAmbientEvent = useCallback((value: unknown): boolean => {
    const simulation = parseFishingWorldDebugSimulation(value)
    if (!simulation || !isEnabledFishingWorldDebugSimulation(simulation)) return false
    return forceAmbientEvent(simulation.kind, simulation.presentation)
  }, [forceAmbientEvent])

  return {
    activeEvents,
    completeAmbientEvent,
    forceAmbientEvent,
    simulateAmbientEvent,
    diagnostics: {
      seed: seed.current,
      nextEligibleAtMs: schedulerState.current.nextEligibleAtMs,
      suppressionReason: lastDecision?.type === 'suppressed' ? lastDecision.reason : null,
      reducedMotion,
      rollout: rolloutObserver.current.snapshot(),
      debugRejection,
    },
  }
}
